const logging = require('@sstdev/lib_logging');
const registry = require('../registry');

// this implements the generic fallback/pass through for all hNode types
// that don't have a specific implementation in the blockGroups
const hNode = { extractAccess, redact };
registry.register('hNode', hNode);
module.exports = hNode;

/**
 * @type {import('../registry').ExtractAccessFunction}
 */
function extractAccess(hNode, options) {
    const { roles, isTab } = options;
    const { hiddenFor, customizableVisibility, children } = hNode;

    let spec = [];
    let access = {};

    // if they manually (explicitly) added customizableVisibility
    // or if this is a tab, let's add it.
    // unless it is the "saved filters" related tabs
    // Note, this assumes it is NOT a navMenu or page level hNode
    if (customizableVisibility || isTab) {
        if (!['Filter By', 'Saved Filters'].includes(hNode.title)) {
            spec = registry.addSpec(spec, hNode, { isTab });
            access = registry.addAccess(access, hNode, { roles, isTab });
        }
    } else {
        // otherwise, if there is a hiddenFor on this level, let them know we can't support that.
        if (hiddenFor) {
            logging.warn(
                `${hNode.hNodeTypeGroup} ${hNode.hNodeType} ${hNode.title} is not a tab or button, but has hiddenFor. Limiting access at that level is not supported at this time.`
            );
        }
    }

    // regardless, just aggregate whatever the children define.
    children?.forEach(child => {
        // If there is a dedicated implementation for the child, use it.
        // otherwise, just fall back to the generic one (this one).
        const extractChildRoles =
            registry[`${child.hNodeTypeGroup}s`]?.[child.hNodeType]?.extractAccess ?? registry.hNode.extractAccess;

        const childAccess = extractChildRoles(child, {
            roles,
            // Ideally, we'd add an blockGroups/layout/tabViewLayout.js to handle this "exception".
            // But it would require copying everything from here, except for this line.
            isTab: hNode.hNodeType === 'TabViewLayout'
        });
        spec = spec.concat(childAccess.spec);
        access = roles.reduce((acc, role) => {
            acc[role] = acc[role] || {};
            acc[role] = { ...acc[role], ...childAccess.access[role] };
            return acc;
        }, access);
    });

    return { spec, access };
}

/**
 * This is the same for everything, except for pages
 * @param {hNode} hNode
 * @param {PagesAccess | {[accessTargetId: string]: boolean} } access
 * @param {boolean} defaultAccess
 * @param {boolean} isTab
 * @returns {hNode} redacted hNode
 */
function redact(hNode, access, defaultAccess, isTab) {
    const { id, customizableVisibility = [], hiddenFor, showForViewPort = [], hideForViewport = [], children } = hNode;

    let accessTargetId = customizableVisibility[0]?.accessTargetId ?? '';
    if (isTab) {
        // tabs are tracked differently, so that both edit and new tabs are treated the same.
        accessTargetId = `tab_${hNode.title}`;
    }

    const limitation = access[id] ?? access[accessTargetId] ?? (hiddenFor?.length > 0 ? defaultAccess : null);
    let viewportRestrictions = {
        showForViewPort,
        hideForViewport
    };

    if (limitation != null) {
        if (typeof limitation === 'boolean') {
            if (!limitation) {
                return undefined;
            }
        } else {
            // if BOTH mobile and desktop are not allowed, redact this node.
            if (!limitation.mobile && !limitation.desktop) {
                return undefined;
            } else if (!limitation.mobile) {
                // only mobile is not allowed, add a view port limitation for mobile
                viewportRestrictions.hideForViewport.push('MOBILE');
            } else if (!limitation.desktop) {
                // only desktop is not allowed, add a view port limitation for desktop
                viewportRestrictions.hideForViewport.push('DESKTOP');
            }
        }
    }
    const childrenAreTabs = hNode.hNodeType === 'TabViewLayout';

    return {
        ...hNode,
        ...viewportRestrictions,
        children: children
            ?.map(child => {
                // navheadings and pages are processed by nacHeadings.js extract(). everything else is processed here.
                const redactChild =
                    registry[`${child.hNodeTypeGroup}s`]?.[child.hNodeType]?.redact ?? registry.hNode.redact;

                return redactChild(child, access, defaultAccess, childrenAreTabs);
            })
            .filter(x => !!x)
    };
}
